import { Component, Input } from '@angular/core'
import { closeJobSelectionDialog } from '../../../shared/utils/job-selection-dialog-utils'
import { Store } from '@ngrx/store'
import { JobApiService } from '../../../shared/services/job-api.service'
import { ToasterService } from '../../../shared/services/toaster.service'
import { Observable, takeUntil, Subject } from 'rxjs'
import {
    JobSelectionListItem,
    selectJobSelectionOptions,
} from '../../../state/selection/selection.selectors'
import { HttpErrorResponse } from '@angular/common/http'
import { JobSelectionRef, PipelineType } from '../../../shared/model/jobs'
import { environment } from '../../../../environments/environment'
import {
    areHierarchicalAncestorRelated,
    hasHierarchicalAncestryConflict,
} from '../../../state/selection/selection.state'

@Component({
    selector: 'app-recurrent-neighborhoods-selection',
    templateUrl: './recurrent-neighborhoods-selection.component.html',
    styleUrls: ['./recurrent-neighborhoods-selection.component.scss'],
})
export class RecurrentNeighborhoodsSelectionComponent {
    @Input()
    siblingDatasets: string[] | never[]
    selectionNames$: Observable<JobSelectionListItem[]>
    private selectedSelections: JobSelectionRef[] | null = null
    private hasAncestryConflict = false

    experimentName = ''
    keepBackground = true
    radius = 1
    numberOfRcn = 1
    submitted = false
    runBatchJob: boolean = false

    private readonly wikiUrl = environment.wikiUrl
    private readonly onDestroy$ = new Subject<void>()

    readonly isSelectionDisabled = (
        item: JobSelectionListItem,
        selected: JobSelectionListItem[]
    ): boolean => selected.some(s => areHierarchicalAncestorRelated(s, item))

    constructor(
        private store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.selectionNames$ = this.store
            .select(selectJobSelectionOptions)
            .pipe(takeUntil(this.onDestroy$))
    }

    get canStart(): boolean {
        return (
            this.radius > 0 &&
            this.experimentName.length > 0 &&
            this.numberOfRcn > 0 &&
            !!this.selectedSelections &&
            this.selectedSelections.length > 0 &&
            !this.hasAncestryConflict &&
            !this.submitted
        )
    }

    ngOnDestroy(): void {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    cancel(): void {
        closeJobSelectionDialog()
    }

    startJob(): void {
        if (!this.canStart) return
        this.submitted = true

        this.jobApi
            .startRecurrentNeighborhoodsJob({
                type: PipelineType.RecurrentNeighborhoods,
                selections: this.selectedSelections!,
                experimentName: this.experimentName,
                keepBackground: this.keepBackground,
                radius: this.radius,
                numberOfRcn: this.numberOfRcn,
                batch: this.runBatchJob,
            })
            .subscribe({
                next: () => closeJobSelectionDialog(),
                error: (err: HttpErrorResponse) => {
                    this.submitted = false
                    this.toaster.httpError(err, 'Error starting job')
                },
            })
    }

    selectSelections(selections: JobSelectionListItem[]): void {
        this.hasAncestryConflict = hasHierarchicalAncestryConflict(selections)
        if (this.hasAncestryConflict) {
            this.toaster.error(
                'Cannot select hierarchical/unsupervised selections that are parent/child (or ancestor/descendant) of each other.',
                'Invalid selection'
            )
            this.selectedSelections = null
            return
        }
        this.selectedSelections = selections.map(({ id, type, path }) => ({ id, type, path }))
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Navigation#recurrent-spatial-neighborhoods-rcns`, '_blank')
    }
}
